import React from "react";

// ── Air module design system ────────────────────────────────────────
// Signature: an "airport departure board" register — every reading (time,
// PNR, flight number, fare, seat code, status) renders in a tabular
// monospace face like a split-flap board; body copy stays on the app's
// existing system font. Navy is inherited from BingoPay's existing brand
// (used app-wide); amber is new and reserved ONLY for status/confirmation
// moments — never for ordinary interactive/selected states, which stay
// navy/sky — so it keeps meaning "this is now real" throughout the flow.

export const AIR = {
  navy: "#0B1A35",
  navyMid: "#14294F",
  sky: "#0EA5E9",
  amber: "#F5A524",
  amberDeep: "#B45309",
  amberSoft: "#FEF3E2",
  paper: "#F7F9FC",
  card: "#FFFFFF",
  ink: "#374151",
  label: "#94A3B8",
  mist: "#E2EAF4",
  green: "#10B981",
  red: "#EF4444",
};

export const AIR_MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

// Mounts once per page — browser dedupes the repeated @import after first load.
export const AirFontImport = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');`}</style>
);

// Tabular-numeral readout — times, PNR, flight numbers, fares, seat codes.
export const Readout = ({ children, size = 14, weight = 600, color = AIR.navy, tracking = "0.02em", style }) => (
  <span style={{ fontFamily: AIR_MONO, fontSize: size, fontWeight: weight, color, letterSpacing: tracking, fontVariantNumeric: "tabular-nums", ...style }}>
    {children}
  </span>
);

// Departure-board style status badge — amber is reserved for this alone.
const STATUS_MAP = {
  CONFIRMED: { label: "CONFIRMED", bg: AIR.amberSoft, fg: AIR.amberDeep, dot: AIR.amber },
  PAYMENT_DONE: { label: "PAYMENT DONE", bg: "#EFF6FF", fg: "#1D4ED8", dot: AIR.sky },
  TICKETING_IN_PROGRESS: { label: "IN PROGRESS", bg: "#FEF3E2", fg: AIR.amberDeep, dot: AIR.amber },
  INITIATED: { label: "INITIATED", bg: "#F1F5F9", fg: AIR.label, dot: AIR.label },
  TICKETING_FAILED: { label: "FAILED", bg: "#FEF2F2", fg: "#B91C1C", dot: AIR.red },
  CANCELLED: { label: "CANCELLED", bg: "#F1F5F9", fg: AIR.label, dot: AIR.label },
  REFUNDED: { label: "REFUNDED", bg: "#F1F5F9", fg: AIR.label, dot: AIR.label },
};

// `label` overrides the display text (e.g. the provider's own StatusDesc)
// while `status` still selects the color bucket — the derived key can be a
// best-effort guess, but the words on screen stay authoritative.
export const StatusBadge = ({ status, label, size = "md" }) => {
  const s = STATUS_MAP[status] || { label: status || "UNKNOWN", bg: "#F1F5F9", fg: AIR.label, dot: AIR.label };
  const pad = size === "sm" ? "3px 8px" : "5px 12px";
  const fontSize = size === "sm" ? 10 : 11;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
      background: s.bg, color: s.fg, padding: pad, borderRadius: 20,
      fontFamily: AIR_MONO, fontSize, fontWeight: 700, letterSpacing: "0.06em",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {(label || s.label || "").toString().toUpperCase()}
    </span>
  );
};

// This backend wraps business-logic failures (wallet insufficient balance,
// wallet debit errors, etc.) in HTTP 202 responses shaped like
// { error_code, error, message, status, action_params } — NOT a 4xx/5xx,
// so axios resolves them as "success" and skips catch(). WeFlyGo-proxied
// endpoints instead fail via { Response_Header: { Error_Code, Error_Desc } }.
// Check both shapes and always return the real message, never a generic one.
export const apiErrorMessage = (res) => {
  if (!res) return null;
  if (res.error_code !== undefined && res.message) return res.message;
  if (res.Response_Header && res.Response_Header.Error_Code !== "0000") {
    return res.Response_Header.Error_Desc || "Request failed.";
  }
  return null;
};

// Distinguishes "this hasn't failed, we just don't know the outcome yet" (e.g. Air_Ticketing
// still processing at the airline) from a hard failure. Callers must not show a generic error
// (or worse, an active "Pay again" button) for this case — payment may already be done.
export const isTicketingInProgress = (res) => res?.error_code === "TICKETING_IN_PROGRESS";

// Perforated tear-line — the boarding-pass signature device, shared so
// every page that shows a ticket stub uses the exact same die-cut.
export const TearLine = ({ background = AIR.navyMid, bg = AIR.paper }) => (
  <div style={{ background, display: "flex", alignItems: "center", height: 20 }}>
    <div style={{ width: 18, height: 18, borderRadius: "50%", background: bg, marginLeft: -9, flexShrink: 0 }} />
    <div style={{ flex: 1, borderTop: "1.5px dashed rgba(255,255,255,0.14)" }} />
    <div style={{ width: 18, height: 18, borderRadius: "50%", background: bg, marginRight: -9, flexShrink: 0 }} />
  </div>
);
